const isMostCriticalDates = (activity) => {
  if (!activity) return;
  activity._skipConflictUpdate = true;

  const updatedLeafTasks = detectUnfitTaskDates(activity);

  applyVisualConflictStatusToGanttTasks(
    updatedLeafTasks,
    window.ganttVisualization,
    activity
  );
};

const detectUnfitTaskDates = (activity) => {
  const activityStart = new Date(activity.start_date);
  const activityEnd = new Date(activity.end_date);
  const tasks = activity.tasks || [];
  const pendingTasks = new Map();

  savePendingValues(tasks, pendingTasks);

  resetUnfitFields(tasks);
  const leafTasks = getAllLeafTasks(tasks);

  leafTasks.forEach((task) => {
    if (pendingTasks.has(task.id)) {
      const pendingValues = pendingTasks.get(task.id);
      task.unfitStartDate = pendingValues.unfitStartDate;
      task.unfitEndDate = pendingValues.unfitEndDate;
    }
  });

  const earlyStartTasks = leafTasks.filter(
    (t) => new Date(t.start_date) < activityStart
  );

  if (earlyStartTasks.length > 0) {
    const minStart = Math.min(
      ...earlyStartTasks.map((t) => new Date(t.start_date).getTime())
    );
    earlyStartTasks
      .filter((t) => new Date(t.start_date).getTime() === minStart)
      .forEach((t) => {
        if (t.modificationSource !== 'pending') {
          t.unfitStartDate = 'early';
        }
      });
  } else {
    const hasExactStart = leafTasks.some(
      (t) => new Date(t.start_date).getTime() === activityStart.getTime()
    );

    if (!hasExactStart) {
      const futureTasks = leafTasks.filter(
        (t) => new Date(t.start_date) > activityStart
      );
      if (futureTasks.length > 0) {
        const minFuture = Math.min(
          ...futureTasks.map((t) => new Date(t.start_date).getTime())
        );
        futureTasks
          .filter((t) => new Date(t.start_date).getTime() === minFuture)
          .forEach((t) => {
            if (t.modificationSource !== 'pending') {
              t.unfitStartDate = 'late';
            }
          });
      }
    }
  }

  const lateEndTasks = leafTasks.filter(
    (t) => new Date(t.end_date) > activityEnd
  );

  if (lateEndTasks.length > 0) {
    const maxEnd = Math.max(
      ...lateEndTasks.map((t) => new Date(t.end_date).getTime())
    );
    lateEndTasks
      .filter((t) => new Date(t.end_date).getTime() === maxEnd)
      .forEach((t) => {
        if (t.modificationSource !== 'pending') {
          t.unfitEndDate = 'late';
        }
      });
  } else {
    const hasExactEnd = leafTasks.some(
      (t) => new Date(t.end_date).getTime() === activityEnd.getTime()
    );

    if (!hasExactEnd) {
      const beforeTasks = leafTasks.filter(
        (t) => new Date(t.end_date) < activityEnd
      );
      if (beforeTasks.length > 0) {
        const maxBefore = Math.max(
          ...beforeTasks.map((t) => new Date(t.end_date).getTime())
        );
        beforeTasks
          .filter((t) => new Date(t.end_date).getTime() === maxBefore)
          .forEach((t) => {
            if (t.modificationSource !== 'pending') {
              t.unfitEndDate = 'early';
            }
          });
      }
    }
  }

  leafTasks.forEach((t) => {
    if (t.children.length > 0) {
      if (t.modificationSource !== 'pending') t.unfitStartDate = null;
      if (t.modificationSource !== 'pending') t.unfitEndDate = null;
    }
  });

  const hasConflict = leafTasks.some((t) => t.unfitStartDate || t.unfitEndDate);
  const ganttParent = window.ganttVisualization.getTask(activity.unique_id);
  if (ganttParent.requestState === 'pending') {
    ganttParent.requestState = 'pending';
  } else {
    ganttParent.requestState = hasConflict ? 'draft' : null;
  }

  return leafTasks;
};

export const applyVisualConflictStatusToGanttTasks = (
  tasks,
  gantt,
  activity
) => {
  if (!Array.isArray(tasks)) return;
  tasks.forEach((t) => {
    if (!gantt.isTaskExists(t.id)) return;
    const ganttTask = gantt.getTask(t.id);

    if (ganttTask.modificationSource !== 'pending') {
      ganttTask.unfitStartDate = t.unfitStartDate || null;
      ganttTask.unfitEndDate = t.unfitEndDate || null;

      const hasConflict = t.unfitStartDate || t.unfitEndDate;
      if (hasConflict) {
        ganttTask.modificationSource = 'draft';
      } else {
        ganttTask.modificationSource = null;
      }
    }

    if (!ganttTask._skipConflictUpdate) {
      gantt.refreshTask(t.id, true);
    }
  });

  if (activity) {
    gantt.updateTask(activity.unique_id);
  }
};

const getAllLeafTasks = (tasks) => {
  const result = [];

  const walk = (list) => {
    list.forEach((task) => {
      result.push(task);
      if (task.children && task.children.length > 0) {
        walk(task.children);
      }
    });
  };

  walk(tasks);
  return result;
};

const resetUnfitFields = (tasks) => {
  tasks.forEach((t) => {
    if (t.modificationSource !== 'pending') t.unfitStartDate = null;
    if (t.modificationSource !== 'pending') t.unfitEndDate = null;
    if (t.children) resetUnfitFields(t.children);
  });
};

const savePendingValues = (taskList, pendingTasks) => {
  taskList.forEach((task) => {
    const isPending = task.modificationSource === 'pending';

    if (isPending) {
      pendingTasks.set(task.id, {
        unfitStartDate: 'pending',
        unfitEndDate: 'pending'
      });
    }

    if (task.children) savePendingValues(task.children, pendingTasks);
  });
};

export { isMostCriticalDates };
